import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Button, Modal, Tabs, Typography, Space, Tag, Collapse, Input, Form, Flex } from "antd"
import { Editor, EditorProvider, Toolbar, BtnBold, BtnItalic, BtnUnderline, createButton } from "react-simple-wysiwyg"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { oldRoutes } from "router/old-routes"
import { Icon } from "ui/Icon"
import { colors } from "themes/variables"
import { AlignCenter } from "ui/Icon/DSIcon/AlignCenter"
import { AlignJustify } from "ui/Icon/DSIcon/AlignJustify"
import { useTranslation } from "react-i18next"
import { serialize } from "object-to-formdata"
import { filter } from "lodash"
import pluralize from "pluralize"
import { sendEmail, sendSms, sendMessage } from "store/clients"
import { hideModal, resetModal, showModal } from "modules/modals/reducer"
import { modalSelector } from "modules/modals/selectors"
import styles from "./ClientsContactWindow.module.css"
import DOMPurify from "dompurify"

const { TabPane } = Tabs
const { Text, Title } = Typography
const { Panel } = Collapse

const ClientContactForm = ({ handleClose, type, clients, userIds, setSelectedRowKeys }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isFormValid, setIsFormValid] = useState(false)
  const selectedClients = useMemo(() => filter(clients, (client) => userIds?.includes(client.user_id)), [userIds, clients])
  const { metadata } = useSelector((store) => store.clients)
  const selectedNames = useMemo(() => selectedClients.map((item) => `${item.user.first_name} ${item.user.last_name}`), [selectedClients])
  const [messageType, setMessageType] = useState("custom")
  const templateModal = useSelector(modalSelector("templateModal"))
  const templates = metadata[`${type}_templates_collection`]
  const [form] = Form.useForm()

  const messageTypeLabels = {
    email: "Email",
    text: "SMS"
  }

  const openHandler = () => dispatch(showModal("templateModal"))
  const closeHandler = () => {
    dispatch(hideModal("templateModal"))
    handleClose()
    setSelectedRowKeys([])
  }

  const onClosedHandler = () => {
    dispatch(resetModal("templateModal"))
    navigate(oldRoutes.guideClientsPath({}, searchParams))
  }

  const handleFormChange = (value) => {
    const isTouched = value ? true : form.isFieldsTouched(true)
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0)
    setIsFormValid(isTouched && !hasErrors)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const data = { client_ids: userIds }

      if (messageType === "template") {
        data.template_id = values.template_id
      } else {
        data.body = values.body
        data.subject = values.subject
      }

      const payloadKey = type === "text" ? "sms" : type
      const formData = serialize({ [payloadKey]: data })

      const fn = { email: sendEmail, text: sendSms, message: sendMessage }
      dispatch(fn[type](formData)).then(() => {
        openHandler()
      })
    } catch (error) {
      console.error("Validation failed:", error)
    }
  }

  const BtnAlignCenter = createButton("Align center", <AlignCenter />, "justifyCenter")
  const BtnAlignJustify = createButton("Align justify", <AlignJustify />, "justifyLeft")
  const messageLabel = messageTypeLabels[type] || "Message"

  return (
    <EditorProvider>
      <Modal
        open={templateModal.isOpen}
        onCancel={closeHandler}
        afterClose={onClosedHandler}
        centered
        footer={[
          <div key="footer" style={{ textAlign: "center" }}>
            <Button key="ok" type="primary" onClick={closeHandler}>
              OK
            </Button>
          </div>
        ]}
        width={"400px"}
      >
        <Title level={4} style={{ textAlign: "center" }}>
          {`${messageLabel} sent to ${pluralize("customer", selectedNames?.length, true)}`}
        </Title>
      </Modal>

      <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={handleFormChange}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Text style={{ display: "block" }} className="mb-xxs">
              {t(`client.${type}`)} {selectedNames?.length} Clients:
            </Text>
            {selectedNames?.map((name) => (
              <Tag color="blue" key={name}>
                {name}
              </Tag>
            ))}
          </div>
          {type !== "message" && (
            <Space>
              <Button type={messageType === "custom" ? "primary" : "default"} variant="filled" onClick={() => setMessageType("custom")}>
                {t(`client.new_${type}`)}
              </Button>
              <Button type={messageType === "template" ? "primary" : "default"} variant="filled" onClick={() => setMessageType("template")}>
                {t("client.templates")}
              </Button>
            </Space>
          )}

          <Tabs activeKey={messageType} onChange={setMessageType} className={styles.contactTabs}>
            <TabPane key="custom">
              {type === "email" && (
                <Form.Item
                  name="subject"
                  label={t("client.subject")}
                  rules={[{ required: true, message: t("client.error_messages.subject_required") }]}
                >
                  <Input placeholder="Enter subject" onBlur={(e) => form.setFieldValue("subject", e.target.value.trim())} />
                </Form.Item>
              )}
              <Flex justify="flex-end" className="mb-s">
                <Button
                  type="primary"
                  disabled={true}
                  icon={<Icon name="aiEnhancement" color={true ? colors.textTertiary : "#ffffff"} height={20} width={20} />}
                >
                  {t("client.enhance_with_ui")}
                </Button>
              </Flex>
              <Form.Item
                name="body"
                rules={[
                  { required: true, message: t("client.error_messages.body_required") },
                  {
                    validator: (_, value) => {
                      // Sanitize the input and check if it's empty or contains only spaces
                      const sanitizedValue = DOMPurify.sanitize(value || "")
                        .replace(/&nbsp;/g, "")
                        .trim()
                      return sanitizedValue !== ""
                        ? Promise.resolve()
                        : Promise.reject(new Error(t("client.error_messages.body_no_spaces")))
                    }
                  }
                ]}
              >
                <Editor
                  style={{ height: 200 }}
                  value={form.getFieldValue("body") || ""}
                  onChange={(e) => {
                    const value = e.target.value.trim() // Trim the value
                    form.setFieldsValue({ body: value }) // Update the form value
                    handleFormChange(value) // Trigger form validation state update
                  }}
                >
                  <Toolbar>
                    <BtnBold />
                    <BtnItalic />
                    <BtnUnderline />
                    <BtnAlignCenter />
                    <BtnAlignJustify />
                  </Toolbar>
                </Editor>
              </Form.Item>

              <Space>
                <Button type="default" onClick={handleClose}>
                  {t("global.cancel")}
                </Button>
                <Button type="primary" htmlType="submit" disabled={!isFormValid}>
                  {t("global.send")}
                </Button>
              </Space>
            </TabPane>

            {type !== "message" && (
              <TabPane key="template">
                {templates?.length > 0 && (
                  <Collapse
                    defaultActiveKey={form.getFieldValue("template_id")}
                    onChange={(key) => form.setFieldsValue({ template_id: key })}
                  >
                    {templates?.map((template) => (
                      <Panel header={template.name} key={template.id.toString()}>
                        <Space direction="vertical">
                          {template.heading && <Title level={5}>{template.heading}</Title>}
                          {template.category !== "automated" && <Text>Hello [Client Name]</Text>}
                          <div>{template.paragraph_text}</div>
                          {template.button_url && template.template_type !== "email" && <Button type="link">{template.button_url}</Button>}
                          <div>{template.signature}</div>
                          <div>
                            <Text strong italic>
                              [Guide profile name]
                            </Text>
                          </div>
                          <Button type="primary" htmlType="submit" className="mt-s w-max">
                            {t("global.send")}
                          </Button>
                        </Space>
                      </Panel>
                    ))}
                  </Collapse>
                )}
              </TabPane>
            )}
          </Tabs>
        </Space>
      </Form>
    </EditorProvider>
  )
}

ClientContactForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["email", "text", "message"]).isRequired,
  clients: PropTypes.array.isRequired,
  userIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  setSelectedRowKeys: PropTypes.array
}

export default ClientContactForm
